/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on event-handler setState */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ScrollText, RefreshCw, Trash2, Filter, CheckCircle, XCircle, AlertTriangle, Info, User, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const EVENT_TYPES = [
  { id: "all", label: "Alle", icon: ScrollText },
  { id: "job", label: "Jobs", icon: Clock },
  { id: "scrape", label: "Scraping", icon: RefreshCw },
  { id: "login", label: "Logins", icon: User },
  { id: "logout", label: "Logouts", icon: User },
];

const STATUS_CONFIG = {
  success: { icon: CheckCircle, color: "text-green-500", bg: "bg-green-500/10" },
  error: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10" },
  warning: { icon: AlertTriangle, color: "text-yellow-500", bg: "bg-yellow-500/10" },
  info: { icon: Info, color: "text-blue-500", bg: "bg-blue-500/10" },
};

function formatTimestamp(isoString) {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

export function LogsTab({ api }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const limit = 50;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      params.append("skip", (page * limit).toString());
      if (filter !== "all") {
        params.append("event_types", filter);
      }
      const res = await api.get(`/logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) {
      toast.error("Fehler beim Laden der Logs");
    } finally {
      setLoading(false);
    }
  }, [api, filter, page]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleCleanup = async () => {
    if (!window.confirm("Logs älter als 30 Tage löschen?")) return;
    try {
      const res = await api.delete("/logs/cleanup?days=30");
      toast.success(res.data.message);
      loadLogs();
    } catch (e) {
      toast.error("Fehler beim Bereinigen");
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div data-testid="logs-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
            System-Logs
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Jobs, Scraping-Vorgänge und Benutzeraktivitäten
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadLogs}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 border border-white/10 text-xs font-bold uppercase hover:border-white/30"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
          <button
            onClick={handleCleanup}
            className="flex items-center gap-2 px-3 py-2 border border-red-500/30 text-red-400 text-xs font-bold uppercase hover:bg-red-500/10"
            data-testid="cleanup-logs-btn"
          >
            <Trash2 className="w-4 h-4" />
            Bereinigen
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {EVENT_TYPES.map(type => (
          <button
            key={type.id}
            onClick={() => { setFilter(type.id); setPage(0); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${
              filter === type.id
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-white/10 text-muted-foreground hover:text-foreground hover:border-white/30'
            }`}
            data-testid={`filter-${type.id}`}
          >
            <type.icon className="w-3.5 h-3.5" />
            {type.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="mb-4 text-xs text-muted-foreground">
        Gesamt: {total} Einträge {filter !== "all" && `(Filter: ${EVENT_TYPES.find(t => t.id === filter)?.label})`}
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ScrollText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Keine Logs gefunden</p>
        </div>
      ) : (
        <div className="space-y-1">
          {logs.map((log, idx) => {
            const statusConfig = STATUS_CONFIG[log.status] || STATUS_CONFIG.info;
            const StatusIcon = statusConfig.icon;
            
            return (
              <div
                key={`${log.timestamp}-${idx}`}
                className={`flex items-start gap-3 p-3 border border-white/5 ${statusConfig.bg}`}
                data-testid={`log-entry-${idx}`}
              >
                <StatusIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${statusConfig.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {log.event_type}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(log.timestamp)}
                    </span>
                  </div>
                  <p className="text-sm mt-1">{log.message}</p>
                  {log.details?.ip && (
                    <p className="text-xs text-muted-foreground mt-1">IP: {log.details.ip}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1.5 text-xs border border-white/10 disabled:opacity-50"
          >
            Zurück
          </button>
          <span className="text-xs text-muted-foreground">
            Seite {page + 1} von {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1.5 text-xs border border-white/10 disabled:opacity-50"
          >
            Weiter
          </button>
        </div>
      )}
    </div>
  );
}
